import { DebugLog } from '../core/DebugLog.js';
import { LevelSystem } from '../systems/LevelSystem.js';

/**
 * HTML overlay panel for allocating stat points on level-up.
 */
export class LevelUpPanel {
  constructor(uiRoot) {
    this.uiRoot = uiRoot;
    this.isOpen = false;
    this.panel = null;
    this.elements = {};
    this._onAllocate = null; // callback: (stat) => void
  }

  /**
   * Open the panel showing available stat points.
   * @param {object} gameState - The GameState singleton
   * @param {Function} onAllocate - Callback when a point is allocated: (stat) => void
   */
  open(gameState, onAllocate) {
    if (this.isOpen) {
      this._refresh(gameState);
      return;
    }

    this._onAllocate = onAllocate;
    this.isOpen = true;
    this._create(gameState);
  }

  _create(gameState) {
    this.panel = document.createElement('div');
    this.panel.id = 'levelup-panel';
    this.panel.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(10, 10, 20, 0.95);
      border: 2px solid #ffdd44;
      padding: 12px 16px;
      pointer-events: auto;
      font-family: 'Courier New', monospace;
      color: #cccccc;
      z-index: 200;
      min-width: 180px;
      text-align: center;
    `;

    const title = document.createElement('div');
    title.style.cssText = `font-size: 14px; font-weight: bold; color: #ffdd44; margin-bottom: 8px;`;
    title.textContent = 'LEVEL UP!';
    this.panel.appendChild(title);

    const pointsLabel = document.createElement('div');
    pointsLabel.style.cssText = `font-size: 10px; color: #aaaaaa; margin-bottom: 10px;`;
    pointsLabel.textContent = `Stat Points: ${gameState.statPoints}`;
    this.elements.pointsLabel = pointsLabel;
    this.panel.appendChild(pointsLabel);

    const stats = [
      { key: 'maxHp', label: '+HP (+5 HP)', color: '#cc3333' },
      { key: 'damage', label: '+Damage (+5%)', color: '#ffaa44' },
      { key: 'speed', label: '+Speed (+3)', color: '#44cccc' },
      { key: 'defense', label: '+Defense (+2%)', color: '#8888cc' },
    ];

    this.buttons = {};

    for (const stat of stats) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
        gap: 8px;
      `;

      const label = document.createElement('span');
      label.style.cssText = `font-size: 10px; color: ${stat.color};`;
      label.textContent = stat.label;

      const count = document.createElement('span');
      count.style.cssText = `font-size: 9px; color: #888888; min-width: 20px; text-align: right;`;
      count.textContent = `(${gameState.statAllocations[stat.key]})`;
      this.elements['count_' + stat.key] = count;

      const btn = document.createElement('button');
      btn.style.cssText = `
        background: rgba(60, 60, 80, 0.8);
        border: 1px solid ${stat.color};
        color: ${stat.color};
        font-family: 'Courier New', monospace;
        font-size: 10px;
        padding: 2px 8px;
        cursor: pointer;
        pointer-events: auto;
      `;
      btn.textContent = '+';
      btn.addEventListener('click', () => this._allocate(stat.key));
      this.buttons[stat.key] = btn;

      row.appendChild(label);
      row.appendChild(count);
      row.appendChild(btn);
      this.panel.appendChild(row);
    }

    const hint = document.createElement('div');
    hint.style.cssText = `font-size: 8px; color: #666666; margin-top: 8px;`;
    hint.textContent = 'Click + to allocate points';
    this.panel.appendChild(hint);

    this.uiRoot.appendChild(this.panel);
  }

  _allocate(stat) {
    if (this._onAllocate) {
      this._onAllocate(stat);
    }
  }

  /**
   * Refresh the panel display with updated stat points.
   * @param {object} gameState - The GameState singleton
   */
  _refresh(gameState) {
    if (!this.isOpen || !this.panel) return;

    if (this.elements.pointsLabel) {
      this.elements.pointsLabel.textContent = `Stat Points: ${gameState.statPoints}`;
    }

    const stats = ['maxHp', 'damage', 'speed', 'defense'];
    for (const key of stats) {
      const countEl = this.elements['count_' + key];
      if (countEl) {
        countEl.textContent = `(${gameState.statAllocations[key]})`;
      }
      // Disable button if no points remain
      if (this.buttons[key]) {
        this.buttons[key].disabled = gameState.statPoints <= 0;
        this.buttons[key].style.opacity = gameState.statPoints <= 0 ? '0.4' : '1';
      }
    }

    // Auto-close if no points remain
    if (gameState.statPoints <= 0) {
      setTimeout(() => this.close(), 300);
    }
  }

  /**
   * Refresh from outside (e.g. after allocation).
   */
  refresh(gameState) {
    this._refresh(gameState);
  }

  /**
   * Close the panel.
   */
  close() {
    if (this.panel && this.panel.parentNode) {
      this.panel.parentNode.removeChild(this.panel);
    }
    this.panel = null;
    this.isOpen = false;
    this.elements = {};
    this.buttons = {};
    this._onAllocate = null;
  }

  /**
   * Destroy and clean up.
   */
  destroy() {
    this.close();
  }
}
